// Sample enemy templates for Phase 1. Five units span the §5.13 race table
// and the §7.5.0 size buckets (mob / elite). Stats use the Lv1 anchors from
// §5.7.3 and scale with powerMult().
import { powerMult, } from '@renavora/shared';
import { getSkill } from './skills.js';
const BOSS_RULES = [
    { category: 'targeting', ruleId: 'target_nearest' },
    { category: 'movement', ruleId: 'move_melee_stick' },
    { category: 'tempo', ruleId: 'tempo_ult_focus' },
    { category: 'threatPolicy', ruleId: 'threat_high' },
    { category: 'positionPolicy', ruleId: 'pos_behind_target' },
];
const MIDBOSS_DOT_RULES = [
    { category: 'targeting', ruleId: 'target_lowest_hp_pct' },
    { category: 'movement', ruleId: 'move_melee_stick' },
    { category: 'tempo', ruleId: 'tempo_dot_focus' },
    { category: 'threatPolicy', ruleId: 'threat_high' },
    { category: 'positionPolicy', ruleId: 'pos_flank_target_left' },
];
const MIDBOSS_BURST_RULES = [
    { category: 'targeting', ruleId: 'target_nearest' },
    { category: 'movement', ruleId: 'move_distance_short' },
    { category: 'tempo', ruleId: 'tempo_burst' },
    { category: 'threatPolicy', ruleId: 'threat_high' },
    { category: 'positionPolicy', ruleId: 'pos_flank_target_right' },
];
const RARE_RULES = [
    { category: 'targeting', ruleId: 'target_attacker' },
    { category: 'movement', ruleId: 'move_distance_mid' },
    { category: 'tempo', ruleId: 'tempo_conditional' },
    { category: 'threatPolicy', ruleId: 'threat_overdrive' },
    { category: 'positionPolicy', ruleId: 'pos_safe_line' },
];
const AMBUSH_LEADER_RULES = [
    { category: 'targeting', ruleId: 'target_lowest_hp' },
    { category: 'movement', ruleId: 'move_melee_stick' },
    { category: 'tempo', ruleId: 'tempo_burst' },
    { category: 'threatPolicy', ruleId: 'threat_high' },
    { category: 'positionPolicy', ruleId: 'pos_behind_target' },
];
const REGULAR_RUSH_RULES = [
    { category: 'targeting', ruleId: 'target_nearest' },
    { category: 'movement', ruleId: 'move_melee_stick' },
    { category: 'tempo', ruleId: 'tempo_burst' },
    { category: 'threatPolicy', ruleId: 'threat_high' },
    { category: 'positionPolicy', ruleId: 'pos_flank_target_right' },
];
const REGULAR_SKIRMISH_RULES = [
    { category: 'targeting', ruleId: 'target_attacker' },
    { category: 'movement', ruleId: 'move_distance_short' },
    { category: 'tempo', ruleId: 'tempo_conditional' },
    { category: 'threatPolicy', ruleId: 'threat_low' },
    { category: 'positionPolicy', ruleId: 'pos_safe_line' },
];
const REGULAR_CASTER_RULES = [
    { category: 'targeting', ruleId: 'target_lowest_hp_pct' },
    { category: 'movement', ruleId: 'move_distance_mid' },
    { category: 'tempo', ruleId: 'tempo_conditional' },
    { category: 'threatPolicy', ruleId: 'threat_low' },
    { category: 'positionPolicy', ruleId: 'pos_safe_line' },
];
const DUNGEON_ENEMY_THEMES = ['forest', 'cave', 'temple', 'tower', 'abyss'];
const DUNGEON_ENEMY_THEME_SET = new Set(DUNGEON_ENEMY_THEMES);
export const REGULAR_MOB_TEMPLATE_IDS_BY_THEME = {
    forest: ['enemy.mob.forest.briarling', 'enemy.mob.forest.thorn-runner'],
    cave: ['enemy.mob.cave.crystal-bat', 'enemy.mob.cave.shale-crawler'],
    temple: ['enemy.mob.temple.candle-acolyte', 'enemy.mob.temple.brass-sentinel'],
    tower: ['enemy.mob.tower.rime-apprentice', 'enemy.mob.tower.mirror-knife'],
    abyss: ['enemy.mob.abyss.drowned-wisp', 'enemy.mob.abyss.grave-crawler'],
};
export const ELITE_TEMPLATE_ID_BY_THEME = {
    forest: 'enemy.elite.forest.ironbark-guard',
    cave: 'enemy.elite.cave.crystal-brute',
    temple: 'enemy.elite.temple.marble-sentinel',
    tower: 'enemy.elite.tower.arcane-duelist',
    abyss: 'enemy.elite.abyss.void-knight',
};
function dungeonThemeOf(template) {
    const parts = template.id.split('.');
    if (parts.length !== 3 || parts[0] !== 'dungeon') {
        throw new Error(`dungeonThemeOf: malformed template id '${template.id}'`);
    }
    const theme = parts[1];
    if (!DUNGEON_ENEMY_THEME_SET.has(theme)) {
        throw new Error(`dungeonThemeOf: unknown theme '${theme}'`);
    }
    return theme;
}
/** Resolve the regular mob pool used by dungeon battle and ambush nodes. */
export function regularMobTemplateIdsForDungeon(template) {
    return REGULAR_MOB_TEMPLATE_IDS_BY_THEME[dungeonThemeOf(template)];
}
/** Resolve the elite template used by dungeon elite roles. */
export function eliteTemplateIdForDungeon(template) {
    return ELITE_TEMPLATE_ID_BY_THEME[dungeonThemeOf(template)];
}
export const ENEMY_TEMPLATES = {
    goblin: {
        id: 'goblin',
        name: 'Goblin',
        creatureType: 'humanoid',
        element: 'physical',
        hpLv1: 80,
        weaponBaseLv1: 4,
        attackInterval: 1.5,
        moveSpeed: 4,
        armorLv1: 5,
    },
    wolf: {
        id: 'wolf',
        name: 'Wolf',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 70,
        weaponBaseLv1: 5,
        attackInterval: 1.0,
        moveSpeed: 6,
        armorLv1: 3,
    },
    skeleton: {
        id: 'skeleton',
        name: 'Skeleton',
        creatureType: 'undead',
        element: 'physical',
        hpLv1: 90,
        weaponBaseLv1: 4,
        attackInterval: 1.4,
        moveSpeed: 3.5,
        armorLv1: 6,
        resistance: { dark: 0.25 },
    },
    fire_sprite: {
        id: 'fire_sprite',
        name: 'Fire Sprite',
        creatureType: 'elemental',
        element: 'fire',
        hpLv1: 60,
        weaponBaseLv1: 6,
        attackInterval: 1.2,
        moveSpeed: 5,
        armorLv1: 2,
        resistance: { fire: 0.5, ice: -0.25 },
    },
    goblin_champion: {
        id: 'goblin_champion',
        name: 'Goblin Champion',
        creatureType: 'humanoid',
        element: 'physical',
        hpLv1: 700,
        weaponBaseLv1: 8,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 30,
        eliteFlag: true,
    },
    // ADR 0015 §4.4: 軸 9 archetype 4 (Finisher) の execute 即死不可対象を実敵で
    // 検証するための boss テンプレ。HP 3000 帯、armor 50、bossFlag: true。
    goblin_warlord: {
        id: 'goblin_warlord',
        name: 'Goblin Warlord',
        creatureType: 'humanoid',
        element: 'physical',
        hpLv1: 3000,
        weaponBaseLv1: 12,
        attackInterval: 1.5,
        moveSpeed: 4,
        armorLv1: 50,
        bossFlag: true,
    },
    // -------------------------------------------------------------------------
    // Phase 5.x-C2 — theme-aware regular mob and elite pools used by dungeon
    // battle / elite / ambush nodes. Each regular mob carries an active skill
    // so the runtime path exercises skill selection instead of pure auto-attack.
    // -------------------------------------------------------------------------
    'enemy.mob.forest.briarling': {
        id: 'enemy.mob.forest.briarling',
        name: 'Briarling',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 95,
        weaponBaseLv1: 5,
        attackInterval: 1.25,
        moveSpeed: 4.7,
        armorLv1: 6,
        itemSkillIds: ['bleed_strike'],
        behaviorRules: MIDBOSS_DOT_RULES,
    },
    'enemy.mob.forest.thorn-runner': {
        id: 'enemy.mob.forest.thorn-runner',
        name: 'Thorn Runner',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 75,
        weaponBaseLv1: 6,
        attackInterval: 1.0,
        moveSpeed: 5.4,
        armorLv1: 4,
        itemSkillIds: ['basic_strike'],
        behaviorRules: REGULAR_RUSH_RULES,
    },
    'enemy.elite.forest.ironbark-guard': {
        id: 'enemy.elite.forest.ironbark-guard',
        name: 'Ironbark Guard',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 720,
        weaponBaseLv1: 9,
        attackInterval: 1.2,
        moveSpeed: 4.2,
        armorLv1: 32,
        itemSkillIds: ['bleed_strike'],
        behaviorRules: MIDBOSS_DOT_RULES,
        eliteFlag: true,
    },
    'enemy.mob.cave.crystal-bat': {
        id: 'enemy.mob.cave.crystal-bat',
        name: 'Crystal Bat',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 70,
        weaponBaseLv1: 6,
        attackInterval: 1.0,
        moveSpeed: 6,
        armorLv1: 3,
        itemSkillIds: ['basic_strike'],
        behaviorRules: REGULAR_SKIRMISH_RULES,
    },
    'enemy.mob.cave.shale-crawler': {
        id: 'enemy.mob.cave.shale-crawler',
        name: 'Shale Crawler',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 110,
        weaponBaseLv1: 5,
        attackInterval: 1.4,
        moveSpeed: 3.6,
        armorLv1: 9,
        itemSkillIds: ['bleed_strike'],
        behaviorRules: MIDBOSS_DOT_RULES,
    },
    'enemy.elite.cave.crystal-brute': {
        id: 'enemy.elite.cave.crystal-brute',
        name: 'Crystal Brute',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 760,
        weaponBaseLv1: 8,
        attackInterval: 1.25,
        moveSpeed: 4,
        armorLv1: 35,
        itemSkillIds: ['basic_strike'],
        behaviorRules: MIDBOSS_BURST_RULES,
        eliteFlag: true,
    },
    'enemy.mob.temple.candle-acolyte': {
        id: 'enemy.mob.temple.candle-acolyte',
        name: 'Candle Acolyte',
        creatureType: 'mech',
        element: 'light',
        hpLv1: 80,
        weaponBaseLv1: 6,
        attackInterval: 1.2,
        moveSpeed: 4.4,
        armorLv1: 5,
        itemSkillIds: ['fireball'],
        behaviorRules: REGULAR_CASTER_RULES,
    },
    'enemy.mob.temple.brass-sentinel': {
        id: 'enemy.mob.temple.brass-sentinel',
        name: 'Brass Sentinel',
        creatureType: 'mech',
        element: 'light',
        hpLv1: 100,
        weaponBaseLv1: 5,
        attackInterval: 1.35,
        moveSpeed: 3.8,
        armorLv1: 10,
        itemSkillIds: ['basic_strike'],
        behaviorRules: REGULAR_RUSH_RULES,
    },
    'enemy.elite.temple.marble-sentinel': {
        id: 'enemy.elite.temple.marble-sentinel',
        name: 'Marble Sentinel',
        creatureType: 'mech',
        element: 'light',
        hpLv1: 740,
        weaponBaseLv1: 8,
        attackInterval: 1.35,
        moveSpeed: 3.8,
        armorLv1: 38,
        itemSkillIds: ['heal'],
        behaviorRules: MIDBOSS_BURST_RULES,
        eliteFlag: true,
    },
    'enemy.mob.tower.rime-apprentice': {
        id: 'enemy.mob.tower.rime-apprentice',
        name: 'Rime Apprentice',
        creatureType: 'humanoid',
        element: 'ice',
        hpLv1: 80,
        weaponBaseLv1: 6,
        attackInterval: 1.2,
        moveSpeed: 4.2,
        armorLv1: 5,
        itemSkillIds: ['fireball'],
        behaviorRules: REGULAR_CASTER_RULES,
    },
    'enemy.mob.tower.mirror-knife': {
        id: 'enemy.mob.tower.mirror-knife',
        name: 'Mirror Knife',
        creatureType: 'humanoid',
        element: 'ice',
        hpLv1: 75,
        weaponBaseLv1: 7,
        attackInterval: 0.95,
        moveSpeed: 5.5,
        armorLv1: 4,
        itemSkillIds: ['basic_strike'],
        behaviorRules: REGULAR_SKIRMISH_RULES,
    },
    'enemy.elite.tower.arcane-duelist': {
        id: 'enemy.elite.tower.arcane-duelist',
        name: 'Arcane Duelist',
        creatureType: 'humanoid',
        element: 'ice',
        hpLv1: 700,
        weaponBaseLv1: 9,
        attackInterval: 1.1,
        moveSpeed: 4.8,
        armorLv1: 30,
        itemSkillIds: ['fireball'],
        behaviorRules: MIDBOSS_BURST_RULES,
        eliteFlag: true,
    },
    'enemy.mob.abyss.drowned-wisp': {
        id: 'enemy.mob.abyss.drowned-wisp',
        name: 'Drowned Wisp',
        creatureType: 'undead',
        element: 'dark',
        hpLv1: 85,
        weaponBaseLv1: 6,
        attackInterval: 1.15,
        moveSpeed: 4.6,
        armorLv1: 5,
        resistance: { dark: 0.25 },
        itemSkillIds: ['flame_burst'],
        behaviorRules: REGULAR_CASTER_RULES,
    },
    'enemy.mob.abyss.grave-crawler': {
        id: 'enemy.mob.abyss.grave-crawler',
        name: 'Grave Crawler',
        creatureType: 'undead',
        element: 'dark',
        hpLv1: 115,
        weaponBaseLv1: 5,
        attackInterval: 1.45,
        moveSpeed: 3.5,
        armorLv1: 10,
        resistance: { dark: 0.35 },
        itemSkillIds: ['bleed_strike'],
        behaviorRules: MIDBOSS_DOT_RULES,
    },
    'enemy.elite.abyss.void-knight': {
        id: 'enemy.elite.abyss.void-knight',
        name: 'Void Knight',
        creatureType: 'undead',
        element: 'dark',
        hpLv1: 780,
        weaponBaseLv1: 9,
        attackInterval: 1.25,
        moveSpeed: 4.1,
        armorLv1: 34,
        resistance: { dark: 0.35 },
        itemSkillIds: ['bleed_strike'],
        behaviorRules: MIDBOSS_DOT_RULES,
        eliteFlag: true,
    },
    // -------------------------------------------------------------------------
    // ADR 0030 §2.5 — theme-aware boss / midboss / rare templates.
    // 5 themes × 3 roles = 15 entries. Stat curves (hp/weapon/armor + flags)
    // come from §2.5.2; attackInterval/moveSpeed/element/creatureType are
    // pinned in Step 10c-boss-replace per the implementation plan
    // (forest/cave=beast/physical, temple=mech/light, tower=humanoid/ice,
    // abyss=undead/dark).
    // -------------------------------------------------------------------------
    // forest (Tier 1 starter)
    'enemy.boss.forest.t1': {
        id: 'enemy.boss.forest.t1',
        name: 'Treant Lord',
        creatureType: 'beast',
        element: 'physical',
        // W1-2 retune (ADR 0042): the uniform 3000/14/60 anchor made the first
        // real boss unwinnable at the recommended level (F2: kill progress 5-20%,
        // 0/24 solo AND party stalemate). T1 boss is the first wall a Lv~10 hero
        // meets; sized so a recommended-level solo kills it in roughly 60-120s
        // with its escort instead of never.
        hpLv1: 1050,
        weaponBaseLv1: 8,
        attackInterval: 1.5,
        moveSpeed: 4,
        armorLv1: 25,
        ultimate: 'ult.druid.wildwarden',
        itemSkillIds: ['bleed_strike'],
        behaviorRules: BOSS_RULES,
        bossFlag: true,
    },
    // Tier-0 tutorial boss (ADR 0038 §2.1 / P55-042). The starter forest boss
    // (`enemy.boss.forest.t1`, Treant Lord — retuned to 1050 HP by ADR 0042
    // W1-2, sized for a recommended-level Lv~10 hero) is still beyond a fresh
    // Lv1 hero. The tutorial reuses every other forest enemy as-is but
    // needs a gentle, low-HP boss so the early-game solo-survival path exists.
    // Keeps `bossFlag` + `BOSS_RULES` (a real boss, not a pure auto-attacker) but
    // no ultimate and modest damage.
    'enemy.boss.forest.t0': {
        id: 'enemy.boss.forest.t0',
        name: 'Treant Sapling',
        creatureType: 'beast',
        element: 'physical',
        // Tuned against the solo onramp harness (t0-onramp.test.ts) for owner intent
        // "T0 = solo 100% for every starting job": a fresh Lv1 hero of ANY Tier 1
        // job levels up across the loop and reliably beats this boss. It fights
        // alone (no escorts for Tier 0, see pickBossEscorts) and is deliberately
        // gentle — the tutorial teaches the loop, it is not a skill check.
        hpLv1: 200,
        weaponBaseLv1: 6,
        attackInterval: 2.0,
        moveSpeed: 4,
        armorLv1: 10,
        ultimate: null,
        behaviorRules: BOSS_RULES,
        bossFlag: true,
    },
    'enemy.midboss.forest.t1': {
        id: 'enemy.midboss.forest.t1',
        name: 'Vine Stalker',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 1500,
        weaponBaseLv1: 10,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 40,
        itemSkillIds: ['bleed_strike'],
        behaviorRules: MIDBOSS_DOT_RULES,
        eliteFlag: true,
    },
    'enemy.rare.forest.t1': {
        id: 'enemy.rare.forest.t1',
        name: 'Moss Behemoth',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 1800,
        weaponBaseLv1: 11,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 45,
        itemSkillIds: ['flame_burst'],
        behaviorRules: RARE_RULES,
        eliteFlag: true,
    },
    // cave (Tier 2 starter)
    // W1-2 retune (ADR 0042): with band-paced levelling the T2 mixed cohort
    // measured above the 60-80% band; hardened slightly.
    'enemy.boss.cave.t2': {
        id: 'enemy.boss.cave.t2',
        name: 'Underking',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 3400,
        weaponBaseLv1: 14,
        attackInterval: 1.5,
        moveSpeed: 4,
        armorLv1: 60,
        ultimate: 'ult.dragon-knight.dragon-breath',
        itemSkillIds: ['basic_strike'],
        behaviorRules: BOSS_RULES,
        bossFlag: true,
    },
    'enemy.midboss.cave.t2': {
        id: 'enemy.midboss.cave.t2',
        name: 'Stone Maw',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 1500,
        weaponBaseLv1: 10,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 40,
        itemSkillIds: ['basic_strike'],
        behaviorRules: MIDBOSS_BURST_RULES,
        eliteFlag: true,
    },
    'enemy.rare.cave.t2': {
        id: 'enemy.rare.cave.t2',
        name: 'Crystal Tyrant',
        creatureType: 'beast',
        element: 'physical',
        hpLv1: 1800,
        weaponBaseLv1: 11,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 45,
        itemSkillIds: ['item.unique.tripwire-volley.contact-aoe'],
        behaviorRules: RARE_RULES,
        eliteFlag: true,
    },
    // temple (Tier 3 starter)
    'enemy.boss.temple.t3': {
        id: 'enemy.boss.temple.t3',
        name: 'Sealed Idol',
        creatureType: 'mech',
        element: 'light',
        // W1-2 retune (ADR 0042): at boss Lv ~34 the 14-anchor auto-attack
        // one-shots recommended-level melee heroes (measured TTK ~3s, 0/24 even
        // with an ally). Damage anchor lowered; HP slightly down so the kill is
        // attainable within the 180s limit.
        hpLv1: 2100,
        weaponBaseLv1: 8,
        attackInterval: 1.5,
        moveSpeed: 4,
        armorLv1: 50,
        ultimate: 'ult.paladin.holy-aegis',
        // W1-2 (ADR 0042): self-heal removed. Boss sustain hard-countered
        // mid-DPS builds at the recommended level (S4 gear-heavy: 12/12
        // time-expired — the 180s fight let healing outpace damage) while fast
        // builds never saw it (knight kills in ~40s). The aegis ult remains the
        // boss identity.
        itemSkillIds: [],
        behaviorRules: BOSS_RULES,
        bossFlag: true,
    },
    'enemy.midboss.temple.t3': {
        id: 'enemy.midboss.temple.t3',
        name: 'Marble Guardian',
        creatureType: 'mech',
        element: 'light',
        hpLv1: 1500,
        weaponBaseLv1: 10,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 40,
        itemSkillIds: ['heal'],
        behaviorRules: MIDBOSS_BURST_RULES,
        eliteFlag: true,
    },
    'enemy.rare.temple.t3': {
        id: 'enemy.rare.temple.t3',
        name: 'Hollow Choir',
        creatureType: 'mech',
        element: 'light',
        hpLv1: 1800,
        weaponBaseLv1: 11,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 45,
        itemSkillIds: ['fireball'],
        behaviorRules: RARE_RULES,
        eliteFlag: true,
    },
    // tower (Tier 4 starter)
    'enemy.boss.tower.t4': {
        id: 'enemy.boss.tower.t4',
        name: 'Inverted Sage',
        creatureType: 'humanoid',
        element: 'ice',
        // W1-2 retune (ADR 0042): T4 measured 100% at the recommended level
        // (above the 60-80% band); hardened toward the band. Sharp threshold:
        // 3300/14 -> 96% solo, 4000/16 -> 17% solo under band-paced levelling.
        hpLv1: 3650,
        weaponBaseLv1: 15,
        attackInterval: 1.5,
        moveSpeed: 4,
        armorLv1: 60,
        ultimate: 'ult.archmage.meteor-shower',
        itemSkillIds: ['fireball'],
        behaviorRules: BOSS_RULES,
        bossFlag: true,
    },
    'enemy.midboss.tower.t4': {
        id: 'enemy.midboss.tower.t4',
        name: 'Spell Echo',
        creatureType: 'humanoid',
        element: 'ice',
        hpLv1: 1500,
        weaponBaseLv1: 10,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 40,
        itemSkillIds: ['fireball'],
        behaviorRules: MIDBOSS_BURST_RULES,
        eliteFlag: true,
    },
    'enemy.rare.tower.t4': {
        id: 'enemy.rare.tower.t4',
        name: 'Aetheric Wraith',
        creatureType: 'humanoid',
        element: 'ice',
        hpLv1: 1800,
        weaponBaseLv1: 11,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 45,
        itemSkillIds: ['flame_burst'],
        behaviorRules: RARE_RULES,
        eliteFlag: true,
    },
    // abyss (Tier 5 starter)
    'enemy.boss.abyss.t5': {
        id: 'enemy.boss.abyss.t5',
        name: 'Echo Sovereign',
        creatureType: 'undead',
        element: 'dark',
        // W1-2 retune (ADR 0042): solo at the recommended level measured 54%
        // (below the 60-80% band); softened toward the band.
        hpLv1: 2400,
        weaponBaseLv1: 13,
        attackInterval: 1.5,
        moveSpeed: 4,
        armorLv1: 55,
        ultimate: 'ult.dark-lord.abyssal-throne',
        itemSkillIds: ['item.unique.shadow-totem.dark-aura'],
        behaviorRules: BOSS_RULES,
        bossFlag: true,
    },
    'enemy.midboss.abyss.t5': {
        id: 'enemy.midboss.abyss.t5',
        name: 'Drowned Herald',
        creatureType: 'undead',
        element: 'dark',
        hpLv1: 1500,
        weaponBaseLv1: 10,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 40,
        itemSkillIds: ['bleed_strike'],
        behaviorRules: MIDBOSS_DOT_RULES,
        eliteFlag: true,
    },
    'enemy.rare.abyss.t5': {
        id: 'enemy.rare.abyss.t5',
        name: 'Abyssal Mawkin',
        creatureType: 'undead',
        element: 'dark',
        hpLv1: 1800,
        weaponBaseLv1: 11,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 45,
        itemSkillIds: ['bleed_strike'],
        behaviorRules: RARE_RULES,
        eliteFlag: true,
    },
    // -------------------------------------------------------------------------
    // ADR 0030 §2.4.3 — ambush enemy lineup. 3 leaders paired with 3 mob
    // escorts at event-ambush entry. Theme-agnostic for the starter pool;
    // theme-aware variants are a follow-up (ADR §3.2).
    // -------------------------------------------------------------------------
    'enemy.ambush.bandit-leader': {
        id: 'enemy.ambush.bandit-leader',
        name: 'Bandit Leader',
        creatureType: 'humanoid',
        element: 'physical',
        hpLv1: 300,
        weaponBaseLv1: 8,
        attackInterval: 1.2,
        moveSpeed: 4.5,
        armorLv1: 25,
        itemSkillIds: ['bleed_strike'],
        behaviorRules: AMBUSH_LEADER_RULES,
        eliteFlag: true,
    },
    'enemy.ambush.shadow': {
        id: 'enemy.ambush.shadow',
        name: 'Shadow Stalker',
        creatureType: 'undead',
        element: 'dark',
        hpLv1: 200,
        weaponBaseLv1: 10,
        attackInterval: 0.9,
        moveSpeed: 5,
        armorLv1: 15,
        itemSkillIds: ['flame_burst'],
        behaviorRules: AMBUSH_LEADER_RULES,
    },
    'enemy.ambush.cultist': {
        id: 'enemy.ambush.cultist',
        name: 'Cultist Zealot',
        creatureType: 'humanoid',
        element: 'dark',
        hpLv1: 180,
        weaponBaseLv1: 12,
        attackInterval: 1.1,
        moveSpeed: 4,
        armorLv1: 12,
        itemSkillIds: ['fireball'],
        behaviorRules: AMBUSH_LEADER_RULES,
    },
};
export const ALL_ENEMY_IDS = Object.keys(ENEMY_TEMPLATES);
function cloneBehaviorRules(rules) {
    return rules?.map((rule) => structuredClone(rule)) ?? [];
}
function resolveEnemyUltimate(templateId, id) {
    if (id == null)
        return null;
    const skill = getSkill(id);
    if (skill === undefined) {
        throw new Error(`Enemy template ${templateId} references unknown ultimate skill: ${id}`);
    }
    if (skill.source !== 'ultimate' || !id.startsWith('ult.')) {
        throw new Error(`Enemy template ${templateId} ultimate must be an ult.* catalogue skill: ${id}`);
    }
    if (skill.kind !== 'active') {
        throw new Error(`Enemy template ${templateId} ultimate must be active: ${id}`);
    }
    return id;
}
function resolveEnemyItemSkills(templateId, ids) {
    return (ids ?? []).map((id) => {
        const skill = getSkill(id);
        if (skill === undefined) {
            throw new Error(`Enemy template ${templateId} references unknown active skill: ${id}`);
        }
        if (skill.kind !== 'active') {
            throw new Error(`Enemy template ${templateId} itemSkillId must be active: ${id}`);
        }
        return {
            templateId: id,
            kind: skill.kind,
            activation: structuredClone(skill.activation),
            values: [],
        };
    });
}
/** Instantiate an Entity from an enemy template at a given level. */
export function makeEnemy(templateId, opts) {
    const t = ENEMY_TEMPLATES[templateId];
    if (!t) {
        throw new Error(`Unknown enemy template: ${templateId}`);
    }
    const pow = powerMult(opts.level);
    const hpMax = t.hpLv1 * pow;
    const stats = {
        hpMax,
        weaponBase: t.weaponBaseLv1 * pow,
        atkBonus: 0,
        elementBonus: {},
        critRate: 0.05,
        critDamage: 1.5,
        armor: t.armorLv1 * pow,
        resistance: t.resistance ?? {},
        accuracy: 0,
        evasion: 0,
        attackInterval: t.attackInterval,
        moveSpeed: t.moveSpeed,
    };
    const entity = {
        id: opts.id,
        side: 'enemy',
        level: opts.level,
        element: t.element,
        weaponElement: t.element,
        pos: opts.pos ?? { x: 0, y: 0 },
        radius: t.radius ?? 0.5,
        velocity: { x: 0, y: 0 },
        hp: hpMax,
        hpMax,
        shields: [],
        stats,
        jobTypes: new Set(),
        creatureType: t.creatureType,
        cooldowns: new Map(),
        buffs: [],
        debuffs: [],
        ultimate: resolveEnemyUltimate(templateId, t.ultimate),
        itemSkills: resolveEnemyItemSkills(templateId, t.itemSkillIds),
        behaviorRules: cloneBehaviorRules(t.behaviorRules),
        speedMods: { actionSpeed: 0, attackSpeed: 0, cooldownRate: 0, moveSpeed: 0 },
        damageReduction: 0,
        summonPowerBonus: 0,
        placeablePowerBonus: 0,
        threatMod: 1,
    };
    // ADR 0015 §4.4: EnemyTemplate の boss/elite flag を Entity に転写。
    if (t.bossFlag === true)
        entity.bossFlag = true;
    if (t.eliteFlag === true)
        entity.eliteFlag = true;
    return entity;
}
//# sourceMappingURL=enemies.js.map